import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Trophy, Zap, Users, Calendar } from "lucide-react";
import CoachingProgramsSection from "@/components/CoachingProgramsSection";
// JuneSpecialSection is intentionally not imported — kept in the codebase for future promos.
// import JuneSpecialSection from "@/components/JuneSpecialSection";

const ServicesPage = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="bg-black min-h-screen pt-24" data-testid="programs-page">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="/assets/images/tyexuvmo_IMG_5009.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="text-center mb-16"
          >
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">Elite Coaching</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              COACHING PROGRAMS
            </h1>
            <p className="text-white/60 max-w-2xl mx-auto text-lg">
              Personalized coaching programs designed for your specific goals. 
              From lifestyle transformations to elite competition prep.
            </p>
          </motion.div>
        </div>
      </section>

      {/* June Special Pricing — DISABLED, reserved for future promos */}
      {/* <JuneSpecialSection /> */}

      {/* ============ OFFICIAL COACHING PROGRAMS (single source of truth) ============ */}
      <CoachingProgramsSection />

      {/* What's Included Section */}
      <section className="py-16 sm:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">The Full Package</p>
            <h2 className="font-display text-5xl sm:text-6xl text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              WHAT'S INCLUDED
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Calendar,
                title: "Personalized Plans",
                desc: "Custom nutrition and training plans designed specifically for your body type, goals, and lifestyle."
              },
              {
                icon: Users,
                title: "Weekly Check-Ins",
                desc: "Regular progress reviews with adjustments to keep you on track toward your goals."
              },
              {
                icon: Zap,
                title: "Direct Support",
                desc: "Access to Chris and his team for questions, guidance, and motivation when you need it."
              },
              {
                icon: Trophy,
                title: "Proven Results",
                desc: "40+ years of experience and hundreds of successful transformations."
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-8 border border-[#1A1A1A] hover:border-[#D4AF37]/30 transition-colors"
              >
                <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 flex items-center justify-center">
                  <item.icon className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-white text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coaching Gallery */}
      <section className="py-16 sm:py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">In Action</p>
            <h2 className="font-display text-5xl sm:text-6xl text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              TRAINING WITH CHRIS
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group relative aspect-[4/5] overflow-hidden"
            >
              <img
                src="/assets/images/tyexuvmo_IMG_5009.jpeg"
                alt="Intense Training Session"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-[#D4AF37] text-sm tracking-wider uppercase">Intensity</p>
                <p className="text-white text-lg">Every Rep Counts</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group relative aspect-[4/5] overflow-hidden"
            >
              <img
                src="/assets/images/r8ut5klc_97bbfbd7-848d-4077-96db-d146633a0a18.jpeg"
                alt="Personal Coaching"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-[#D4AF37] text-sm tracking-wider uppercase">Dedication</p>
                <p className="text-white text-lg">Personal Attention</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group relative aspect-[4/5] overflow-hidden"
            >
              <img
                src="/assets/images/5spjl919_IMG_5008.jpeg"
                alt="Female Athletes Training"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-[#D4AF37] text-sm tracking-wider uppercase">Excellence</p>
                <p className="text-white text-lg">Push Your Limits</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-black relative">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/assets/images/9i47ro1u_a466dcc8-b56b-47c6-869c-69fbcbd9562b.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-6">Ready to Transform?</p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              START YOUR JOURNEY
            </h2>
            <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
              Apply now and take the first step toward becoming the best version of yourself.
            </p>
            
            <Link
              to="/assessment"
              className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-10 py-4 text-sm tracking-wider uppercase font-semibold hover:bg-white transition-colors duration-300"
              data-testid="cta-apply-btn"
            >
              Apply for Coaching
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
